/**
 * OneStamp web API client.
 *
 * Phase 4 Part B — web frontend for Brand Owner + Super Admin only.
 * Customer + cashier flows are mobile-only forever, so this client has
 * NO customer or cashier endpoints.
 *
 * Auth pattern:
 *   - JWT token persisted by the auth store; pass it via `auth.token`.
 *   - Every 401 invokes the registered logout helper (set during boot).
 */
const BASE_URL = (process.env.REACT_APP_BACKEND_URL ?? '').replace(/\/$/, '');

let _on401 = null;
export function setOn401(handler) { _on401 = handler; }

// A3 (2026-06-27) — global 403 gate interceptor hook. The AuthStore
// subscribes to these two callbacks on mount so a 403 anywhere in the
// customer surface carrying the structured auth-gate codes redirects
// without each individual page having to catch and route.
let _onProfileCompletionRequired = null;
let _onPasswordChangeRequired = null;
export function setOnProfileCompletionRequired(handler) {
  _onProfileCompletionRequired = handler;
}
export function setOnPasswordChangeRequired(handler) {
  _onPasswordChangeRequired = handler;
}

async function request(path, init = {}) {
  const { token, headers, ...rest } = init;
  const finalHeaders = { Accept: 'application/json', ...headers };
  // Only auto-set JSON Content-Type when the body is a JSON-shaped value.
  // For FormData / Blob / ArrayBuffer / URLSearchParams the browser must
  // pick the Content-Type itself so the multipart boundary (or the right
  // binary type) lands in the header. Forcing 'application/json' over a
  // FormData body was the source of the 422 on /api/uploads/image.
  const body = rest.body;
  const isAutoTypedBody =
    (typeof FormData !== 'undefined' && body instanceof FormData) ||
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams);
  if (body && !isAutoTypedBody && !finalHeaders['Content-Type']) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (token) finalHeaders.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE_URL}${path}`, { ...rest, headers: finalHeaders });
  // Clone before reading — defensive against any external fetch interceptor
  // (e.g. observability scripts) that also reads the body. Without
  // `.clone()`, our `res.text()` would throw "body stream already read".
  let text = '';
  try {
    text = await res.clone().text();
  } catch {
    try { text = await res.text(); } catch { text = ''; }
  }
  let data = null;
  if (text) { try { data = JSON.parse(text); } catch { data = text; } }
  if (res.status === 401 && _on401) {
    try { _on401(); } catch { /* swallow */ }
  }
  if (!res.ok) {
    const detail = formatError(data) ?? `Request failed (${res.status})`;
    // A3 (2026-06-27) — surface the backend's structured auth-gate codes
    // on 403 so the AuthStore can redirect to Complete Profile / Change
    // Password without each caller having to inspect the ApiError.
    if (res.status === 403 && data && typeof data === 'object') {
      const code =
        data.detail && typeof data.detail === 'object' && !Array.isArray(data.detail)
          ? data.detail.code
          : null;
      if (code === 'profile_completion_required' && _onProfileCompletionRequired) {
        try { _onProfileCompletionRequired(); } catch { /* swallow */ }
      } else if (code === 'password_change_required' && _onPasswordChangeRequired) {
        try { _onPasswordChangeRequired(); } catch { /* swallow */ }
      }
    }
    const err = new Error(typeof detail === 'string' ? detail : 'Request failed');
    err.detail = detail;
    err.status = res.status;
    err.raw = data;
    // Attach the structured `code` (if any) so screens can switch on it
    // without re-parsing `raw`.
    err.code =
      data && data.detail && typeof data.detail === 'object' && !Array.isArray(data.detail)
        ? data.detail.code ?? null
        : null;
    throw err;
  }
  return data;
}

function formatError(data) {
  if (!data) return null;
  if (typeof data === 'string') return data;
  const detail = data.detail;
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  // Backend convention for app-level errors: {detail: {code, message}}.
  // Prefer the human-readable `message`, fall back to `msg`.
  if (detail && typeof detail.message === 'string') return detail.message;
  if (detail && typeof detail.msg === 'string') return detail.msg;
  // Pydantic v2 validation errors: {detail: [{loc, msg, type, ...}]}.
  // Collapse to a single line per item with the field path so an operator
  // can still pinpoint which field failed.
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        if (!e) return '';
        const loc =
          Array.isArray(e.loc) && e.loc.length
            ? `${e.loc[e.loc.length - 1]}: `
            : '';
        return `${loc}${e.msg || e.message || JSON.stringify(e)}`;
      })
      .filter(Boolean)
      .join(' · ');
  }
  return JSON.stringify(detail);
}

/**
 * Binary-aware request — returns a { blob, filename } pair derived from
 * Content-Disposition. Used by the CRM export endpoints (CSV / XLSX).
 */
async function requestBlob(path, init = {}) {
  const { token, headers, ...rest } = init;
  const finalHeaders = { ...headers };
  if (token) finalHeaders.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE_URL}${path}`, { ...rest, headers: finalHeaders });
  if (res.status === 401 && _on401) {
    try { _on401(); } catch { /* swallow */ }
  }
  if (!res.ok) {
    let text = '';
    try { text = await res.clone().text(); }
    catch { try { text = await res.text(); } catch { text = ''; } }
    let data = null;
    try { data = JSON.parse(text); } catch { data = text; }
    const detail = formatError(data) ?? `Export failed (${res.status})`;
    const err = new Error(typeof detail === 'string' ? detail : 'Export failed');
    err.detail = detail; err.status = res.status; err.raw = data;
    throw err;
  }
  const disp = res.headers.get('Content-Disposition') || '';
  const m = disp.match(/filename="?([^";]+)"?/i);
  const filename = m ? m[1] : 'export';
  let blob;
  try { blob = await res.clone().blob(); }
  catch { blob = await res.blob(); }
  return { blob, filename };
}

/**
 * URL querystring helper — drops null/undefined/'' values, encodes
 * booleans as `true`/`false`, and prepends `?`.
 */
function qsFromFilters(filters = {}) {
  const qs = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (typeof v === 'boolean') qs.set(k, v ? 'true' : 'false');
    else qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export const API_BASE = BASE_URL;

export const api = {
  // ---- Auth (only public surface, used by Login + boot) ----
  login: (email, password) =>
    request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  // A3 (2026-06-27) — identifier-based login. Backend accepts
  // `{identifier, password}` and auto-detects email vs phone by the
  // presence of `@`. Kept alongside `login()` so existing callers in
  // the staff pages don't break.
  loginIdentifier: (identifier, password) =>
    request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password }),
    }),
  // Customer self-signup (post-cutover P0).
  // Always sends `device_fingerprint: null` per operator Decision §5;
  // web has no native install-id, and the backend silently treats null
  // as no fingerprint (referral anti-abuse only kicks in when both a
  // referral_code AND a fingerprint are present — neither of which
  // this web wrapper passes).
  customerRegister: ({ name, email, password, phone }) =>
    request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        name,
        email,
        password,
        phone: phone ?? null,
        device_fingerprint: null,
      }),
    }),
  // A3 (2026-06-27) — Google ID-token sign-in.
  // Backend: POST /api/customer/auth/google
  googleSignIn: (idToken) =>
    request('/api/customer/auth/google', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken }),
    }),
  // A3 (2026-06-27) — Apple ID-token sign-in. `email` + `full_name`
  // only come from Apple on first consent.
  appleSignIn: (idToken, extras = {}) =>
    request('/api/customer/auth/apple', {
      method: 'POST',
      body: JSON.stringify({
        id_token: idToken,
        email:     extras.email ?? undefined,
        full_name: extras.full_name ?? undefined,
      }),
    }),
  // 2026-06-28 — Emergent Managed Google adapter. Exchanges an Emergent
  // Auth `session_id` (returned in the #session_id= fragment on the
  // hosted-redirect callback) for a OneStamp TokenResponse. Backend:
  // POST /api/customer/auth/emergent-session.
  emergentSessionExchange: (sessionId) =>
    request('/api/customer/auth/emergent-session', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    }),
  // A3 (2026-06-27) — Complete Profile. Required after any registration
  // or social sign-up before loyalty endpoints will accept the token.
  completeProfile: (token, body) =>
    request('/api/customer/auth/complete-profile', {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  // A3 (2026-06-27) — Change Password. Required when
  // `password_is_temporary === true`.
  changePassword: (token, body) =>
    request('/api/customer/auth/change-password', {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),
  me: (token) => request('/api/auth/me', { token }),

  // ---- Brand owner — server scopes everything to JWT's brand_id ----
  ownerOverview: (token, range = '7d') =>
    request(`/api/owner/overview?range=${encodeURIComponent(range)}`, { token }),
  // EX-B Step 6 — admin per-brand drilldown analytics (mirrors owner overview).
  adminBrandOverview: (token, brandId, range = '7d') =>
    request(`/api/admin/brands/${encodeURIComponent(brandId)}/overview?range=${encodeURIComponent(range)}`, { token }),
  // EX-C Step 5 — inactive customers (owner-scoped via JWT brand_id).
  // EX-C Step 6 will add the admin mirror `adminBrandInactiveCustomers`.
  ownerInactiveCustomers: (token, days = 30, limit = 50, offset = 0) =>
    request(
      `/api/owner/inactive-customers?days=${encodeURIComponent(days)}&limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`,
      { token },
    ),
  // EX-C Step 6 — admin per-brand inactive customers (mirrors owner
  // helper; server-side strips `email` to null on every item).
  adminBrandInactiveCustomers: (token, brandId, days = 30, limit = 50, offset = 0) =>
    request(
      `/api/admin/brands/${encodeURIComponent(brandId)}/inactive-customers?days=${encodeURIComponent(days)}&limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`,
      { token },
    ),
  ownerRecentActivity: (token, limit = 20) =>
    request(`/api/owner/recent-activity?limit=${limit}`, { token }),
  ownerAlerts: (token) => request('/api/owner/alerts', { token }),

  // ===========================================================================
  // Merchant Pro V1 PR-2 — Dashboard aggregator + Reservations CRUD
  // ---------------------------------------------------------------------------
  // 2 new endpoints introduced by PR-2 (rest are reused from PR-1 / B.1.x):
  //   • /api/owner/dashboard/today — aggregator for the 4 KPI tiles +
  //     Today at a Glance metrics + 3-row Upcoming + 3-row Waitlist Now.
  //   • /api/owner/reservations/{id} — single-resource read for deep-link
  //     load of the Reservation Detail page.
  // ===========================================================================
  ownerDashboardToday: (token) =>
    request('/api/owner/dashboard/today', { token }),

  ownerReservations: (token, params = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.set('date_from', params.date_from);
    if (params.date_to) q.set('date_to', params.date_to);
    if (params.branch_id) q.set('branch_id', params.branch_id);
    if (params.status) q.set('status', params.status);
    if (params.customer_id) q.set('customer_id', params.customer_id);
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.offset != null) q.set('offset', String(params.offset));
    const qs = q.toString();
    return request(`/api/owner/reservations${qs ? `?${qs}` : ''}`, { token });
  },

  ownerReservationGet: (token, reservationId) =>
    request(
      `/api/owner/reservations/${encodeURIComponent(reservationId)}`,
      { token },
    ),

  ownerReservationApprove: (token, reservationId) =>
    request(
      `/api/owner/reservations/${encodeURIComponent(reservationId)}/approve`,
      { method: 'POST', token, body: {} },
    ),

  ownerReservationReject: (token, reservationId, cancellation_reason) =>
    request(
      `/api/owner/reservations/${encodeURIComponent(reservationId)}/reject`,
      { method: 'POST', token, body: { cancellation_reason } },
    ),

  // Cashier check-in endpoint reused for owner-side check-in CTA (per
  // PR-2 spec — owner has 🟢 on reservations management; cashier endpoint
  // already admits brand_owner via earlier role-allow-list patterns;
  // verify in implementation).
  cashierReservationCheckIn: (token, reservationId) =>
    request(
      `/api/cashier/reservations/${encodeURIComponent(reservationId)}/check-in`,
      { method: 'POST', token, body: {} },
    ),

  // ===========================================================================
  // Merchant Pro V1 PR-3 — Walk-in / Waitlist + Manual Booking
  // ---------------------------------------------------------------------------
  //   • /api/owner/waitlists/buckets         (V3-E1 tabbed view + bottom stats)
  //   • /api/cashier/waitlists               (V3-A2 host-stand add walk-in)
  //   • /api/cashier/waitlists/{id}/seat     (V3-A3 atomic seat → reservation)
  //   • /api/cashier/reservations/manual     (V3-A1 7-step wizard commit)
  //   • /api/owner/crm/customers?search=...  (existing — fast-path lookup)
  //   • /api/brands/{id}/branches            (existing — populate wizard S2)
  //   • /api/owner/rooms/{room_id}/tables    (existing — wizard S3 floorplan)
  // ===========================================================================
  ownerWaitlistBuckets: (token, params = {}) => {
    const q = new URLSearchParams();
    if (params.tab) q.set('tab', params.tab);
    if (params.branch_id) q.set('branch_id', params.branch_id);
    if (params.limit != null) q.set('limit', String(params.limit));
    return request(
      `/api/owner/waitlists/buckets?${q.toString()}`,
      { token },
    );
  },

  cashierWaitlistAddWalkIn: (token, body) =>
    request('/api/cashier/waitlists', {
      method: 'POST', token, body: JSON.stringify(body),
    }),

  cashierWaitlistSeat: (token, waitlistId, body) =>
    request(
      `/api/cashier/waitlists/${encodeURIComponent(waitlistId)}/seat`,
      { method: 'POST', token, body: JSON.stringify(body) },
    ),

  cashierManualBooking: (token, body) =>
    request('/api/cashier/reservations/manual', {
      method: 'POST', token, body: JSON.stringify(body),
    }),

  // Reused from owner CRM — fast-path search consumed by the
  // wizard's S1 Customer Lookup modal.
  ownerCrmSearch: (token, search) => {
    const q = new URLSearchParams();
    if (search) q.set('search', search);
    q.set('page_size', '10');
    return request(`/api/owner/crm/customers?${q.toString()}`, { token });
  },

  // Wizard S2 needs branches + branch rooms; reuses existing endpoints.
  brandBranches: (token, brandId) =>
    request(`/api/brands/${encodeURIComponent(brandId)}/branches`, { token }),

  // Wizard S3 needs the room's tables + the branch's rooms. The
  // owner-scoped endpoints are used because the public read endpoints
  // gate on `brand.pro_enabled`, which is not set for all demo brands.
  ownerRoomTables: (token, brandId, roomId) =>
    request(
      `/api/owner/brands/${encodeURIComponent(brandId)}/rooms/${encodeURIComponent(roomId)}/tables`,
      { token },
    ),

  ownerBranchRooms: (token, brandId, branchId) =>
    request(
      `/api/owner/brands/${encodeURIComponent(brandId)}/branches/${encodeURIComponent(branchId)}/rooms`,
      { token },
    ),

  // ===========================================================================
  // PR-Audit-02B G-02 + G-03 (2026-06-19) — Room & Table CRUD wiring.
  // Endpoints already existed in backend (`/api/owner/brands/{brand_id}/...`);
  // these are the matching frontend client methods. No new endpoints
  // introduced — reuse-first single-touch.
  // ===========================================================================
  ownerCreateRoom: (token, brandId, branchId, body) =>
    request(
      `/api/owner/brands/${encodeURIComponent(brandId)}/branches/${encodeURIComponent(branchId)}/rooms`,
      { method: 'POST', token, body: JSON.stringify(body) },
    ),

  ownerUpdateRoom: (token, brandId, roomId, body) =>
    request(
      `/api/owner/brands/${encodeURIComponent(brandId)}/rooms/${encodeURIComponent(roomId)}`,
      { method: 'PATCH', token, body: JSON.stringify(body) },
    ),

  ownerDeleteRoom: (token, brandId, roomId) =>
    request(
      `/api/owner/brands/${encodeURIComponent(brandId)}/rooms/${encodeURIComponent(roomId)}`,
      { method: 'DELETE', token },
    ),

  ownerCreateTable: (token, brandId, roomId, body) =>
    request(
      `/api/owner/brands/${encodeURIComponent(brandId)}/rooms/${encodeURIComponent(roomId)}/tables`,
      { method: 'POST', token, body: JSON.stringify(body) },
    ),

  ownerUpdateTable: (token, brandId, tableId, body) =>
    request(
      `/api/owner/brands/${encodeURIComponent(brandId)}/tables/${encodeURIComponent(tableId)}`,
      { method: 'PATCH', token, body: JSON.stringify(body) },
    ),

  ownerDeleteTable: (token, brandId, tableId) =>
    request(
      `/api/owner/brands/${encodeURIComponent(brandId)}/tables/${encodeURIComponent(tableId)}`,
      { method: 'DELETE', token },
    ),

  // ===========================================================================
  // Merchant Pro V1 PR-4a — Live Table State Layer
  // ---------------------------------------------------------------------------
  //   • V4-A1  PATCH /api/owner/tables/{id}/state
  //   • V4-A2  GET   /api/owner/branches/{branch_id}/floor-plan/state
  //   • V4-A3  POST  /api/cashier/tables/{id}/unblock
  //   • V4-A4  POST  /api/cashier/reservations/{id}/move-table
  // ===========================================================================
  ownerFloorPlanState: (token, branchId) =>
    request(
      `/api/owner/branches/${encodeURIComponent(branchId)}/floor-plan/state`,
      { token },
    ),

  ownerTableStatePatch: (token, tableId, body) =>
    request(
      `/api/owner/tables/${encodeURIComponent(tableId)}/state`,
      { method: 'PATCH', token, body: JSON.stringify(body) },
    ),

  cashierTableUnblock: (token, tableId, body = {}) =>
    request(
      `/api/cashier/tables/${encodeURIComponent(tableId)}/unblock`,
      { method: 'POST', token, body: JSON.stringify(body) },
    ),

  cashierReservationMoveTable: (token, reservationId, body) =>
    request(
      `/api/cashier/reservations/${encodeURIComponent(reservationId)}/move-table`,
      { method: 'POST', token, body: JSON.stringify(body) },
    ),

  // ===========================================================================
  // Merchant Pro V1.1 WP-5 — Session lifecycle endpoints
  // ---------------------------------------------------------------------------
  //   • POST /api/cashier/reservations/{id}/session/{start,pause,resume,end}
  // All 4 routes ride under the existing /api/cashier/ namespace; cashier,
  // game_master, brand_owner, super_admin can call them (capability +
  // brand-toggle layered enforcement happens server-side).
  // ===========================================================================
  cashierSessionStart: (token, reservationId, body = {}) =>
    request(
      `/api/cashier/reservations/${encodeURIComponent(reservationId)}/session/start`,
      { method: 'POST', token, body: JSON.stringify(body) },
    ),

  cashierSessionPause: (token, reservationId, body = {}) =>
    request(
      `/api/cashier/reservations/${encodeURIComponent(reservationId)}/session/pause`,
      { method: 'POST', token, body: JSON.stringify(body) },
    ),

  cashierSessionResume: (token, reservationId, body = {}) =>
    request(
      `/api/cashier/reservations/${encodeURIComponent(reservationId)}/session/resume`,
      { method: 'POST', token, body: JSON.stringify(body) },
    ),

  cashierSessionEnd: (token, reservationId, body = {}) =>
    request(
      `/api/cashier/reservations/${encodeURIComponent(reservationId)}/session/end`,
      { method: 'POST', token, body: JSON.stringify(body) },
    ),

  // ===========================================================================
  // Merchant Pro V1 PR-4b — Owner Notification Center
  // ---------------------------------------------------------------------------
  //   • V4-B1  GET   /api/owner/notifications
  //   • V4-B2  POST  /api/owner/notifications/mark-read
  // ===========================================================================
  ownerNotifications: (token, params = {}) => {
    const search = new URLSearchParams();
    if (params.category)        search.set('category', params.category);
    if (params.unread_only)     search.set('unread_only', 'true');
    if (params.limit)           search.set('limit', String(params.limit));
    if (params.offset)          search.set('offset', String(params.offset));
    if (params.lookback_days)   search.set('lookback_days', String(params.lookback_days));
    if (params.brand_id)        search.set('brand_id', params.brand_id);
    const qs = search.toString();
    return request(
      `/api/owner/notifications${qs ? `?${qs}` : ''}`,
      { token },
    );
  },

  ownerNotificationsMarkRead: (token, body) =>
    request(
      '/api/owner/notifications/mark-read',
      { method: 'POST', token, body: JSON.stringify(body) },
    ),

  // ===========================================================================
  // PHASE W-1 — Customer Web Shell
  // ---------------------------------------------------------------------------
  // Thin wrappers around the customer-facing endpoints already exposed in
  // OpenAPI. Every helper takes the JWT as its first arg so brand_id scoping
  // and audit attribution stay server-side (the customer JWT carries the
  // identity; these endpoints never accept an `?as_customer=` override).
  // ===========================================================================
  customerBrands: (token, opts = {}) => {
    // Phase W-3 — optional `category_id` filter. Calling without opts
    // preserves the legacy "all active brands" contract used by W-1.
    const qs = new URLSearchParams();
    if (opts.category_id) qs.set('category_id', opts.category_id);
    const s = qs.toString();
    return request(`/api/brands${s ? `?${s}` : ''}`, { token });
  },
  customerBrandsFeatured: (token) => request('/api/brands/featured', { token }),
  customerBrand: (token, brandId) =>
    request(`/api/brands/${encodeURIComponent(brandId)}`, { token }),
  customerBrandBranches: (token, brandId) =>
    request(`/api/brands/${encodeURIComponent(brandId)}/branches`, { token }),
  customerBrandRewards: (token, brandId) =>
    request(`/api/brands/${encodeURIComponent(brandId)}/rewards`, { token }),
  // Phase 2c — rewards catalog + redemption flow (web).
  //
  // `customerMyRewards` covers the cross-brand view in /customer/rewards
  // and the joined-card status path on the detail page. Optional
  // `brand_id` filter narrows server-side to a single joined brand.
  //
  // `customerRedeem` posts `{ request_id }` exactly as the backend's
  // RedeemRequest pydantic model expects. The caller MUST generate
  // `request_id` once (e.g. `crypto.randomUUID()`) and pass that same
  // value on retries — the backend's idempotency cache keys off it and
  // any auto-regen would risk a double-debit.
  customerMyRewards: (token, opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.brand_id) qs.set('brand_id', opts.brand_id);
    const s = qs.toString();
    return request(`/api/me/rewards${s ? `?${s}` : ''}`, { token });
  },
  customerRedeem: (token, rewardId, requestId) =>
    request(`/api/me/rewards/${encodeURIComponent(rewardId)}/redeem`, {
      method: 'POST',
      token,
      body: JSON.stringify({ request_id: requestId }),
    }),
  // Phase 2d — read-only redemption catalog (My Redemptions list +
  // detail). The backend returns server-derived `status` per row
  // (`active` / `used` / `expired`) so client-side filtering is just
  // a `===` comparison. No per-id detail endpoint exists — the detail
  // page mirrors mobile and finds the row by id from the same list
  // response.
  customerRedemptions: (token) => request('/api/me/redemptions', { token }),
  // Phase 2d — customer notifications inbox.
  //
  // `unread_only` and `offset` are intentionally NOT exposed in the
  // wrapper signature: operator decisions §1+§2 lock mobile parity
  // (single fetch of 50, no filter toggle). If a future surface needs
  // them they can be added as named opts without breaking callers.
  customerNotifications: (token) =>
    request('/api/me/notifications?limit=50', { token }),
  customerNotificationsUnreadCount: (token) =>
    request('/api/me/notifications/unread-count', { token }),
  customerMarkNotificationRead: (token, nid) =>
    request(`/api/me/notifications/${encodeURIComponent(nid)}/read`, {
      method: 'POST',
      token,
    }),
  customerMarkAllNotificationsRead: (token) =>
    request('/api/me/notifications/read-all', {
      method: 'POST',
      token,
    }),
  customerCards: (token) => request('/api/me/cards', { token }),
  customerJoinCard: (token, brandId) =>
    request('/api/me/cards/join', {
      method: 'POST', token, body: JSON.stringify({ brand_id: brandId }),
    }),
  // Phase 2b — accepts either a number (legacy limit) or an opts object
  // `{ limit, brand_id }`. Backward-compatible with W-1/HomePage which
  // passes a bare number. The `brand_id` filter lands on the 2a-extended
  // backend query param (Card Detail uses this to scope the activity
  // feed to one card without pulling the full feed and filtering on
  // the client).
  customerRecentActivity: (token, optsOrLimit = {}) => {
    const opts =
      typeof optsOrLimit === 'number'
        ? { limit: optsOrLimit }
        : (optsOrLimit ?? {});
    const qs = new URLSearchParams();
    if (opts.limit != null) qs.set('limit', String(opts.limit));
    if (opts.brand_id) qs.set('brand_id', opts.brand_id);
    const s = qs.toString();
    return request(
      `/api/me/recent-activity${s ? `?${s}` : ''}`,
      { token },
    );
  },
  // Phase 2b — rich transaction history (mobile parity). Wraps the
  // existing `/api/me/transactions/history` endpoint identified in the
  // 2a recon. All filters are server-side; client builds the URL.
  customerTransactionsHistory: (token, opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.type) qs.set('type', opts.type);
    if (opts.brand_id) qs.set('brand_id', opts.brand_id);
    if (opts.from) qs.set('from', opts.from);
    if (opts.to) qs.set('to', opts.to);
    if (opts.q) qs.set('q', opts.q);
    if (opts.limit != null) qs.set('limit', String(opts.limit));
    if (opts.offset != null) qs.set('offset', String(opts.offset));
    const s = qs.toString();
    return request(
      `/api/me/transactions/history${s ? `?${s}` : ''}`,
      { token },
    );
  },
  customerQrSession: (token, customerCardId) =>
    request('/api/me/qr-session', {
      method: 'POST', token, body: JSON.stringify({ customer_card_id: customerCardId }),
    }),
  customerFavorites: (token) => request('/api/me/favorites', { token }),
  customerAddFavorite: (token, brandId) =>
    request('/api/me/favorites', {
      method: 'POST', token, body: JSON.stringify({ brand_id: brandId }),
    }),
  customerRemoveFavorite: (token, brandId) =>
    request(`/api/me/favorites/${encodeURIComponent(brandId)}`, {
      method: 'DELETE', token,
    }),

  // ===========================================================================
  // Phase R3 (Customer) + R4 (Owner) — Receipt OCR Claim
  // ---------------------------------------------------------------------------
  // Backend: /app/backend/receipt_claims.py (SEALED). Six endpoints:
  //   • Customer:
  //       POST   /api/customer/receipt-claims          (multipart: image + brand_id)
  //       GET    /api/customer/receipt-claims           (list, optional ?status=)
  //       GET    /api/customer/receipt-claims/{id}
  //   • Owner / Super Admin:
  //       GET    /api/owner/receipt-claims              (list + counts, filters)
  //       GET    /api/owner/receipt-claims/{id}
  //       POST   /api/owner/receipt-claims/{id}/approve
  //       POST   /api/owner/receipt-claims/{id}/reject  body:{reason, notes?}
  //
  // NON-NEGOTIABLE: the AI vision never auto-awards — it only sets the
  // claim to `pending_owner_review`. Owner approve mints the stamp via
  // the sealed loyalty engine adapter (receipt_claims_stamp_adapter.py).
  // ===========================================================================
  customerCreateReceiptClaim: (token, brandId, file) => {
    const fd = new FormData();
    fd.append('image', file);
    fd.append('brand_id', brandId);
    return request('/api/customer/receipt-claims', {
      method: 'POST',
      token,
      body: fd,
      headers: {}, // let fetch set multipart boundary
    });
  },
  customerListReceiptClaims: (token, opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.status) qs.set('status', opts.status);
    const s = qs.toString();
    return request(
      `/api/customer/receipt-claims${s ? `?${s}` : ''}`,
      { token },
    );
  },
  customerGetReceiptClaim: (token, claimId) =>
    request(
      `/api/customer/receipt-claims/${encodeURIComponent(claimId)}`,
      { token },
    ),

  ownerListReceiptClaims: (token, opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.status)        qs.set('status', opts.status);
    if (opts.limit != null) qs.set('limit',  String(opts.limit));
    if (opts.offset != null)qs.set('offset', String(opts.offset));
    // Phase R10.2 / R10.3 — new query filters.
    if (opts.branch_id)     qs.set('branch_id', opts.branch_id);
    if (opts.dedup_flag)    qs.set('dedup_flag', opts.dedup_flag);
    if (opts.date_from)     qs.set('date_from', opts.date_from);
    if (opts.date_to)       qs.set('date_to',   opts.date_to);
    const s = qs.toString();
    return request(
      `/api/owner/receipt-claims${s ? `?${s}` : ''}`,
      { token },
    );
  },
  // Phase R10.2 / R10.3 — Owner dashboard aggregates & actions.
  ownerReceiptClaimCounts: (token, opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.branch_id) qs.set('branch_id', opts.branch_id);
    if (opts.date_from) qs.set('date_from', opts.date_from);
    if (opts.date_to)   qs.set('date_to',   opts.date_to);
    const s = qs.toString();
    return request(
      `/api/owner/receipt-claims/counts${s ? `?${s}` : ''}`,
      { token },
    );
  },
  ownerBranchPerformance: (token, opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.date_from)  qs.set('date_from',  opts.date_from);
    if (opts.date_to)    qs.set('date_to',    opts.date_to);
    if (opts.branch_ids) qs.set('branch_ids', opts.branch_ids);
    const s = qs.toString();
    return request(
      `/api/owner/receipt-claims/branch-performance${s ? `?${s}` : ''}`,
      { token },
    );
  },
  // R10.6 (2026-07-05) — Analytics page receipt-summary strip.
  ownerAnalyticsReceiptSummary: (token, opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.date_from) qs.set('date_from', opts.date_from);
    if (opts.date_to)   qs.set('date_to',   opts.date_to);
    const s = qs.toString();
    return request(
      `/api/owner/analytics/receipt-summary${s ? `?${s}` : ''}`,
      { token },
    );
  },
  ownerBulkApprovePreview: (token, opts = {}) =>
    request('/api/owner/receipt-claims/bulk-approve', {
      method: 'POST', token,
      body: JSON.stringify({ dry_run: true, branch_id: opts.branch_id ?? null }),
    }),
  ownerBulkApproveExecute: (token, claim_ids) =>
    request('/api/owner/receipt-claims/bulk-approve', {
      method: 'POST', token,
      body: JSON.stringify({ dry_run: false, claim_ids }),
    }),
  ownerConfirmDifferentTransaction: (token, claimId, reason) =>
    request(
      `/api/owner/receipt-claims/${encodeURIComponent(claimId)}/confirm-different-transaction`,
      { method: 'POST', token, body: JSON.stringify({ reason: reason ?? null }) },
    ),
  ownerConfirmDuplicate: (token, claimId, reason) =>
    request(
      `/api/owner/receipt-claims/${encodeURIComponent(claimId)}/confirm-duplicate`,
      { method: 'POST', token, body: JSON.stringify({ reason }) },
    ),
  // R10.4b (2026-07-05) — undo a `confirmed_duplicate` within 24h.
  ownerUndoConfirmDuplicate: (token, claimId, reason) =>
    request(
      `/api/owner/receipt-claims/${encodeURIComponent(claimId)}/undo-confirm-duplicate`,
      { method: 'POST', token, body: JSON.stringify({ reason }) },
    ),

  // -------------------------------------------------------------------
  // R10.4c (2026-07-05) — Outlet Mapping master.
  // -------------------------------------------------------------------
  ownerListUnmappedOutlets: (token) =>
    request('/api/owner/outlet-mappings/unmapped', { token }),

  ownerListOutletMappings: (token, { status = 'all', search = '' } = {}) => {
    const qs = new URLSearchParams();
    if (status && status !== 'all') qs.set('status', status);
    if (search) qs.set('search', search);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/api/owner/outlet-mappings${suffix}`, { token });
  },

  ownerCreateOutletMapping: (
    token, { originalDetectedOutlet, mappedBranchId, applyToPending = true },
  ) =>
    request('/api/owner/outlet-mappings', {
      method: 'POST', token,
      body: JSON.stringify({
        original_detected_outlet: originalDetectedOutlet,
        mapped_branch_id:         mappedBranchId,
        apply_to_pending:         applyToPending,
      }),
    }),

  ownerPatchOutletMapping: (token, mappingId, patch) =>
    request(`/api/owner/outlet-mappings/${encodeURIComponent(mappingId)}`, {
      method: 'PATCH', token, body: JSON.stringify(patch),
    }),

  ownerGetOutletMappingHistory: (token, mappingId) =>
    request(
      `/api/owner/outlet-mappings/${encodeURIComponent(mappingId)}/history`,
      { token },
    ),

  // R10.4d (2026-07-05) — Apply mapping to EXISTING unassigned claims
  // (approved + rejected + pending_owner_review). Dry-run returns a
  // count+breakdown preview; live mutates branch_id only.
  ownerApplyOutletMappingToExisting: (token, mappingId, { dryRun = true } = {}) =>
    request(
      `/api/owner/outlet-mappings/${encodeURIComponent(mappingId)}/apply-to-existing`,
      {
        method: 'POST', token,
        body: JSON.stringify({ dry_run: !!dryRun }),
      },
    ),
  ownerTagBranch: (token, claimId, branchId) =>
    request(
      `/api/owner/receipt-claims/${encodeURIComponent(claimId)}/branch`,
      { method: 'POST', token, body: JSON.stringify({ branch_id: branchId }) },
    ),
  // Owner-scoped brand settings (R10.1).
  ownerGetBrandSettings: (token, brandId) =>
    request(`/api/owner/brands/${encodeURIComponent(brandId)}/settings`, { token }),
  ownerPatchBrandAutoApproval: (token, brandId, body) =>
    request(`/api/owner/brands/${encodeURIComponent(brandId)}/auto-approval`, {
      method: 'PATCH', token, body: JSON.stringify(body),
    }),
  // Owner branches list (reused for filter dropdown + branch tagging).
  ownerListBranches: (token) =>
    request('/api/owner/branches', { token }),
  ownerGetReceiptClaim: (token, claimId) =>
    request(
      `/api/owner/receipt-claims/${encodeURIComponent(claimId)}`,
      { token },
    ),
  ownerApproveReceiptClaim: (token, claimId) =>
    request(
      `/api/owner/receipt-claims/${encodeURIComponent(claimId)}/approve`,
      { method: 'POST', token },
    ),
  ownerRejectReceiptClaim: (token, claimId, body) =>
    request(
      `/api/owner/receipt-claims/${encodeURIComponent(claimId)}/reject`,
      { method: 'POST', token, body: JSON.stringify(body) },
    ),

  // Phase R8 (2026-07-02) — Owner OCR correction + reopen.
  // Backend endpoints:
  //   PATCH /api/owner/receipt-claims/{id}/correction  body:{corrections}
  //   POST  /api/owner/receipt-claims/{id}/reopen      body:{}
  //
  // `corrections` is a flat dict keyed by `ai_extraction.<field>`:
  //   { "ai_extraction.receipt_no": "PC-FULL-123",
  //     "ai_extraction.total_amount": 114922,
  //     "ai_extraction.order_type":   "DINE_IN", ... }
  // Only ship fields the Owner actually edited — any key not sent is left
  // untouched server-side. See R8_RECEIPT_CORRECTION_PREFLIGHT_2026-07-02.md
  // for the full allow-list.
  ownerCorrectReceiptClaim: (token, claimId, corrections) =>
    request(
      `/api/owner/receipt-claims/${encodeURIComponent(claimId)}/correction`,
      { method: 'PATCH', token, body: JSON.stringify({ corrections }) },
    ),
  ownerReopenReceiptClaim: (token, claimId) =>
    request(
      `/api/owner/receipt-claims/${encodeURIComponent(claimId)}/reopen`,
      { method: 'POST', token, body: JSON.stringify({}) },
    ),

  // Apple 5.1.1(v) — account deletion (W-Apple)
  requestAccountDeletion: (token, body) =>
    request('/api/me/account/delete-request', {
      method: 'POST', token, body: JSON.stringify(body),
    }),
  getAccountDeletionStatus: (token) =>
    request('/api/me/account/delete-status', { token }),
  cancelAccountDeletion: (token) =>
    request('/api/me/account/cancel-deletion', {
      method: 'POST', token, body: JSON.stringify({}),
    }),

  // Phase W-2 — promotions surfaces. Optional opts mirror the backend
  // query params (brand_id, include_expired). Calling without opts yields
  // the legacy active-only response, which the W-1 home strip relied on.
  customerPromotions: (token, opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.brand_id) qs.set('brand_id', opts.brand_id);
    if (opts.include_expired) qs.set('include_expired', 'true');
    const s = qs.toString();
    return request(`/api/promotions${s ? `?${s}` : ''}`, { token });
  },
  customerPromotionDetail: (token, id) =>
    request(`/api/promotions/${encodeURIComponent(id)}`, { token }),

  ownerBrand: (token) => request('/api/owner/brand', { token }),
  ownerPatchBrand: (token, body) =>
    request('/api/owner/brand', { method: 'PATCH', token, body: JSON.stringify(body) }),

  // Merchant Pro RBAC Sub-phase C1 (2026-06-18) — effective capability
  // resolver. Consumed by lib/capabilities.js#useEffectiveCapabilities.
  ownerMeCapabilities: (token) => request('/api/owner/me/capabilities', { token }),

  // Merchant Pro RBAC Sub-phase C3 (2026-06-18) — Staff role + user CRUD.
  ownerStaffCapabilities: (token) =>
    request('/api/owner/staff/capabilities', { token }),
  ownerStaffRoles: (token, includeInactive = false, brandId = null) => {
    const params = new URLSearchParams({
      include_inactive: includeInactive ? 'true' : 'false',
    });
    // PR-Audit-02B G-08 — brand_id honored only for super_admin on
    // backend; brand_owner remains pinned to their JWT brand.
    if (brandId) params.set('brand_id', brandId);
    return request(`/api/owner/staff/roles?${params.toString()}`, { token });
  },
  ownerStaffRoleDetail: (token, roleId) =>
    request(`/api/owner/staff/roles/${encodeURIComponent(roleId)}`, { token }),
  ownerCreateStaffRole: (token, body) =>
    request('/api/owner/staff/roles', { method: 'POST', token, body: JSON.stringify(body) }),
  ownerPatchStaffRole: (token, roleId, body) =>
    request(`/api/owner/staff/roles/${encodeURIComponent(roleId)}`, {
      method: 'PATCH', token, body: JSON.stringify(body),
    }),
  ownerCloneStaffRole: (token, roleId, body) =>
    request(`/api/owner/staff/roles/${encodeURIComponent(roleId)}/clone`, {
      method: 'POST', token, body: JSON.stringify(body),
    }),
  ownerDeactivateStaffRole: (token, roleId) =>
    request(`/api/owner/staff/roles/${encodeURIComponent(roleId)}/deactivate`, {
      method: 'POST', token,
    }),
  ownerStaffUsers: (token, { role, status, brandId } = {}) => {
    const params = new URLSearchParams();
    if (role) params.set('role', role);
    if (status) params.set('status', status);
    // PR-Audit-02B G-08 — same SA-only widening as ownerStaffRoles.
    if (brandId) params.set('brand_id', brandId);
    const qs = params.toString();
    return request(`/api/owner/staff/users${qs ? `?${qs}` : ''}`, { token });
  },
  ownerCreateStaffUser: (token, body) =>
    request('/api/owner/staff/users', { method: 'POST', token, body: JSON.stringify(body) }),
  ownerPatchStaffUser: (token, userId, body) =>
    request(`/api/owner/staff/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH', token, body: JSON.stringify(body),
    }),

  ownerBranches: (token) => request('/api/owner/branches', { token }),
  ownerCreateBranch: (token, body) =>
    request('/api/owner/branches', { method: 'POST', token, body: JSON.stringify(body) }),
  ownerPatchBranch: (token, id, body) =>
    request(`/api/owner/branches/${id}`, { method: 'PATCH', token, body: JSON.stringify(body) }),
  ownerDeleteBranch: (token, id) =>
    request(`/api/owner/branches/${id}`, { method: 'DELETE', token }),

  ownerCashiers: (token) => request('/api/owner/cashiers', { token }),
  ownerCreateCashier: (token, body) =>
    request('/api/owner/cashiers', { method: 'POST', token, body: JSON.stringify(body) }),
  ownerPatchCashier: (token, id, body) =>
    request(`/api/owner/cashiers/${id}`, { method: 'PATCH', token, body: JSON.stringify(body) }),
  ownerResetCashierPassword: (token, id, new_password) =>
    request(`/api/owner/cashiers/${id}/reset-password`, {
      method: 'POST', token, body: JSON.stringify({ new_password }),
    }),
  ownerDeleteCashier: (token, id) =>
    request(`/api/owner/cashiers/${id}`, { method: 'DELETE', token }),

  ownerRewards: (token) => request('/api/owner/rewards', { token }),
  ownerCreateReward: (token, body) =>
    request('/api/owner/rewards', { method: 'POST', token, body: JSON.stringify(body) }),
  ownerPatchReward: (token, id, body) =>
    request(`/api/owner/rewards/${id}`, { method: 'PATCH', token, body: JSON.stringify(body) }),
  ownerActivateReward: (token, id) =>
    request(`/api/owner/rewards/${id}/activate`, { method: 'POST', token }),
  ownerDeactivateReward: (token, id) =>
    request(`/api/owner/rewards/${id}/deactivate`, { method: 'POST', token }),
  ownerDeleteReward: (token, id) =>
    request(`/api/owner/rewards/${id}`, { method: 'DELETE', token }),

  ownerPromotions: (token) => request('/api/owner/promotions', { token }),
  ownerCreatePromotion: (token, body) =>
    request('/api/owner/promotions', { method: 'POST', token, body: JSON.stringify(body) }),
  ownerPatchPromotion: (token, id, body) =>
    request(`/api/owner/promotions/${id}`, { method: 'PATCH', token, body: JSON.stringify(body) }),
  ownerDeletePromotion: (token, id) =>
    request(`/api/owner/promotions/${id}`, { method: 'DELETE', token }),

  ownerReviewLinks: (token) => request('/api/owner/review-links', { token }),
  ownerPatchReviewLink: (token, branchId, body) =>
    request(`/api/owner/review-links/${branchId}`, {
      method: 'PATCH', token, body: JSON.stringify(body),
    }),
  ownerReviewAnalytics: (token, range = '30d') =>
    request(`/api/owner/review-analytics?range=${encodeURIComponent(range)}`, { token }),

  // ===========================================================================
  // Phase R7 (2026-07-01) — Google Review prompt (customer-facing)
  // ---------------------------------------------------------------------------
  // Mirrors the mobile helpers in `/app/mobile/src/api.ts` so the ported
  // web `ReviewPromptModal` component consumes the identical backend
  // contract (`backend/rewards.py:663` + `:918`). No new backend code
  // beyond the R7 pure-addition `_r7_receipt_submit_evaluator`.
  //
  // Backend `trigger_event` enum post-R7:
  //   stamp_earned | reward_redeemed | session_open | receipt_submitted.
  // R7 ships `receipt_submitted` as the dedicated trigger for the
  // claim-submit success flow (via the new `on_receipt_submit` rule).
  // ===========================================================================
  reviewPromptShouldShow: (token, { brandId, branchId, triggerEvent }) => {
    const qs = new URLSearchParams();
    qs.set('brand_id', brandId);
    if (branchId) qs.set('branch_id', branchId);
    qs.set('trigger_event', triggerEvent);
    return request(`/api/me/review-prompt/should-show?${qs.toString()}`, { token });
  },
  reviewPromptTrack: (token, { reviewLinkId, action }) =>
    request('/api/me/review-prompt/track', {
      method: 'POST',
      token,
      body: JSON.stringify({ review_link_id: reviewLinkId, action }),
    }),

  // ---- Phase 5A — Birthday Promotions (owner) ----
  ownerBirthdayPromotions: (token) =>
    request('/api/owner/birthday-promotions', { token }),
  ownerCreateBirthdayPromotion: (token, body) =>
    request('/api/owner/birthday-promotions', {
      method: 'POST', token, body: JSON.stringify(body),
    }),
  ownerPatchBirthdayPromotion: (token, id, body) =>
    request(`/api/owner/birthday-promotions/${id}`, {
      method: 'PATCH', token, body: JSON.stringify(body),
    }),
  ownerActivateBirthdayPromotion: (token, id) =>
    request(`/api/owner/birthday-promotions/${id}/activate`, {
      method: 'POST', token,
    }),
  ownerDeactivateBirthdayPromotion: (token, id) =>
    request(`/api/owner/birthday-promotions/${id}/deactivate`, {
      method: 'POST', token,
    }),
  ownerDeleteBirthdayPromotion: (token, id) =>
    request(`/api/owner/birthday-promotions/${id}`, { method: 'DELETE', token }),

  // ---- Super admin — client passes brand_id explicitly when drilling ----
  adminOverview: (token) => request('/api/admin/overview', { token }),

  adminBrands: (token) => request('/api/admin/brands', { token }),
  adminCreateBrand: (token, body) =>
    request('/api/admin/brands', { method: 'POST', token, body: JSON.stringify(body) }),
  adminPatchBrand: (token, id, body) =>
    request(`/api/admin/brands/${id}`, { method: 'PATCH', token, body: JSON.stringify(body) }),
  adminActivateBrand: (token, id) =>
    request(`/api/admin/brands/${id}/activate`, { method: 'POST', token }),
  adminDeactivateBrand: (token, id) =>
    request(`/api/admin/brands/${id}/deactivate`, { method: 'POST', token }),

  adminOwners: (token) => request('/api/admin/owners', { token }),
  adminResetOwnerPassword: (token, id, new_password) =>
    request(`/api/admin/owners/${id}/reset-password`, {
      method: 'POST', token, body: JSON.stringify({ new_password }),
    }),
  adminActivateOwner: (token, id) =>
    request(`/api/admin/owners/${id}/activate`, { method: 'POST', token }),
  adminDeactivateOwner: (token, id) =>
    request(`/api/admin/owners/${id}/deactivate`, { method: 'POST', token }),
  // Soft-delete brand. `confirmName` is type-to-confirm guard sent as
  // ?confirm=<exact-brand-name>. Cascades to branches + owner + cashiers.
  adminDeleteBrand: (token, id, confirmName) =>
    request(
      `/api/admin/brands/${id}?confirm=${encodeURIComponent(confirmName)}`,
      { method: 'DELETE', token },
    ),
  // Restore a soft-deleted brand + its cascade.
  adminRestoreBrand: (token, id) =>
    request(`/api/admin/brands/${id}/restore`, { method: 'POST', token }),
  // Extend subscription by N days (30/90/180/360). Reactivates the
  // brand if it was auto-deactivated by lazy-expiry.
  adminExtendBrand: (token, id, days) =>
    request(`/api/admin/brands/${id}/extend`, {
      method: 'POST', token, body: JSON.stringify({ days }),
    }),
  // SA Pro Toggle UI (Phase SA_PRO_TOGGLE_UI 2026-06-19) — flip
  // `Brand.pro_enabled` boolean via the existing SA-gated endpoint at
  // `admin.py:663`. Backend emits `pro_enabled_toggled` audit row.
  adminToggleBrandPro: (token, id, pro_enabled, reason) =>
    request(`/api/admin/brands/${id}/pro`, {
      method: 'PATCH', token,
      body: JSON.stringify({ pro_enabled, reason: reason ?? null }),
    }),
  // Soft-delete owner. `confirmName` must match the owner's brand name.
  adminDeleteOwner: (token, id, confirmName) =>
    request(
      `/api/admin/owners/${id}?confirm=${encodeURIComponent(confirmName)}`,
      { method: 'DELETE', token },
    ),

  // Phase W-3 — Brand categories taxonomy.
  categories: (token) => request('/api/categories', { token }),
  adminCategories: (token, opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.include_inactive) qs.set('include_inactive', 'true');
    if (opts.include_deleted) qs.set('include_deleted', 'true');
    const s = qs.toString();
    return request(`/api/admin/categories${s ? `?${s}` : ''}`, { token });
  },
  adminCreateCategory: (token, body) =>
    request('/api/admin/categories', {
      method: 'POST', token, body: JSON.stringify(body),
    }),
  adminUpdateCategory: (token, id, body) =>
    request(`/api/admin/categories/${id}`, {
      method: 'PATCH', token, body: JSON.stringify(body),
    }),
  adminDeleteCategory: (token, id) =>
    request(`/api/admin/categories/${id}`, {
      method: 'DELETE', token,
    }),

  adminAuditLogs: (token, filters = {}) => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : '';
    return request(`/api/admin/audit-logs${suffix}`, { token });
  },

  adminEventFailures: (token, filters = {}) => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : '';
    return request(`/api/admin/event-failures${suffix}`, { token });
  },
  adminEventFailureDetail: (token, id) =>
    request(`/api/admin/event-failures/${id}`, { token }),
  adminRetryEventFailure: (token, id) =>
    request(`/api/admin/event-failures/${id}/retry`, { method: 'POST', token }),
  adminDiscardEventFailure: (token, id) =>
    request(`/api/admin/event-failures/${id}/discard`, { method: 'POST', token }),

  // ---- Image upload (brand_owner OR super_admin) ----
  uploadImage: (token, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return request('/api/uploads/image', {
      method: 'POST',
      token,
      body: fd,
      headers: {}, // let fetch set multipart boundary
    });
  },

  // ---- Brand Gallery (P1: owner-managed customer-visible photo strip) ----
  ownerGalleryList: (token, brandId) =>
    request(`/api/owner/brands/${brandId}/gallery`, { token }),
  ownerGalleryUpload: (token, brandId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return request(`/api/owner/brands/${brandId}/gallery`, {
      method: 'POST',
      token,
      body: fd,
      headers: {},
    });
  },
  ownerGalleryReorder: (token, brandId, order) =>
    request(`/api/owner/brands/${brandId}/gallery/reorder`, {
      method: 'PATCH',
      token,
      body: { order },
    }),
  ownerGallerySetActive: (token, brandId, photoId, isActive) =>
    request(`/api/owner/brands/${brandId}/gallery/${photoId}`, {
      method: 'PATCH',
      token,
      body: { is_active: isActive },
    }),
  ownerGalleryDelete: (token, brandId, photoId) =>
    request(`/api/owner/brands/${brandId}/gallery/${photoId}`, {
      method: 'DELETE',
      token,
    }),

  // ---- Phase 5B.1 — Customer CRM (Owner: brand_id derived from JWT) ----
  ownerCrmCustomers: (token, filters = {}) =>
    request(`/api/owner/crm/customers${qsFromFilters(filters)}`, { token }),
  ownerCrmKpis: (token) => request('/api/owner/crm/kpis', { token }),
  ownerCrmCustomer: (token, customerId) =>
    request(`/api/owner/crm/customers/${customerId}`, { token }),
  ownerCrmCustomerTransactions: (token, customerId, filters = {}) =>
    request(
      `/api/owner/crm/customers/${customerId}/transactions${qsFromFilters(filters)}`,
      { token },
    ),
  ownerCrmCustomerRewards: (token, customerId, filters = {}) =>
    request(
      `/api/owner/crm/customers/${customerId}/rewards${qsFromFilters(filters)}`,
      { token },
    ),
  ownerCrmPatchPhone: (token, customerId, phone) =>
    request(`/api/owner/crm/customers/${customerId}/phone`, {
      method: 'PATCH', token, body: JSON.stringify({ phone }),
    }),
  // Reset Stamps — owner uses session brand; admin must pass brandId.
  // Single source-of-truth on the server (`_reset_card_points`).
  ownerCrmResetPoints: (token, customerId) =>
    request(`/api/owner/crm/customers/${customerId}/reset-stamps`, {
      method: 'POST', token,
    }),
  adminCrmResetPoints: (token, brandId, customerId) =>
    request(
      `/api/admin/crm/customers/${customerId}/reset-stamps?brand_id=${encodeURIComponent(brandId)}`,
      { method: 'POST', token },
    ),
  // Customer delete — owner-only, brand-scoped "unjoin from this brand".
  // Single source-of-truth on the server (`_delete_customer_from_brand`).
  ownerCrmDeleteCustomer: (token, customerId) =>
    request(`/api/owner/crm/customers/${customerId}`, {
      method: 'DELETE', token, body: JSON.stringify({ confirmation: 'DELETE' }),
    }),
  ownerCrmBulkDeleteCustomers: (token, customerIds) =>
    request('/api/owner/crm/customers/bulk-delete', {
      method: 'POST', token,
      body: JSON.stringify({ customer_ids: customerIds, confirmation: 'DELETE' }),
    }),
  ownerCrmSettings: (token) =>
    request('/api/owner/brand/crm-settings', { token }),
  ownerCrmPatchSettings: (token, body) =>
    request('/api/owner/brand/crm-settings', {
      method: 'PATCH', token, body: JSON.stringify(body),
    }),
  ownerCrmExportBlob: (token, filters = {}) =>
    requestBlob(`/api/owner/crm/export${qsFromFilters(filters)}`, { token }),

  // ---- Phase 5B.1 — Customer CRM (Super Admin: brand_id REQUIRED) ----
  adminBrandBranches: (token, brandId, includeFixture = false) =>
    request(
      `/api/admin/brands/${brandId}/branches${qsFromFilters(
        includeFixture ? { include_fixture: true } : {},
      )}`,
      { token },
    ),
  adminCrmCustomers: (token, brandId, filters = {}) =>
    request(
      `/api/admin/crm/customers${qsFromFilters({ brand_id: brandId, ...filters })}`,
      { token },
    ),
  adminCrmKpis: (token, brandId, includeFixture = false) =>
    request(
      `/api/admin/crm/kpis${qsFromFilters({
        brand_id: brandId,
        ...(includeFixture ? { include_fixture: true } : {}),
      })}`,
      { token },
    ),
  adminCrmCustomer: (token, brandId, customerId) =>
    request(
      `/api/admin/crm/customers/${customerId}${qsFromFilters({ brand_id: brandId })}`,
      { token },
    ),
  adminCrmCustomerTransactions: (token, brandId, customerId, filters = {}) =>
    request(
      `/api/admin/crm/customers/${customerId}/transactions${qsFromFilters({
        brand_id: brandId,
        ...filters,
      })}`,
      { token },
    ),
  adminCrmCustomerRewards: (token, brandId, customerId, filters = {}) =>
    request(
      `/api/admin/crm/customers/${customerId}/rewards${qsFromFilters({
        brand_id: brandId,
        ...filters,
      })}`,
      { token },
    ),
  adminCrmPatchPhone: (token, brandId, customerId, phone) =>
    request(
      `/api/admin/crm/customers/${customerId}/phone${qsFromFilters({ brand_id: brandId })}`,
      { method: 'PATCH', token, body: JSON.stringify({ phone }) },
    ),
  adminCrmSettings: (token, brandId) =>
    request(
      `/api/admin/crm/brand/crm-settings${qsFromFilters({ brand_id: brandId })}`,
      { token },
    ),
  adminCrmPatchSettings: (token, brandId, body) =>
    request(
      `/api/admin/crm/brand/crm-settings${qsFromFilters({ brand_id: brandId })}`,
      { method: 'PATCH', token, body: JSON.stringify(body) },
    ),
  adminCrmExportBlob: (token, brandId, filters = {}) =>
    requestBlob(
      `/api/admin/crm/export${qsFromFilters({ brand_id: brandId, ...filters })}`,
      { token },
    ),

  // ===========================================================================
  // OneStamp Pro Phase 1 — Memberships (Owner / Cashier / Customer)
  // ---------------------------------------------------------------------------
  // Server scopes brand_id from the JWT for brand_owner / cashier; super_admin
  // must always pass `brand_id` explicitly. Frontend always passes it when
  // present in the user object so admin and owner share one call shape.
  // ===========================================================================
  ownerMembershipTiers: (token, brandId) =>
    request(`/api/owner/memberships/tiers${qsFromFilters({ brand_id: brandId })}`, { token }),
  ownerMembershipTierCreate: (token, brandId, body) =>
    request(`/api/owner/memberships/tiers${qsFromFilters({ brand_id: brandId })}`, {
      method: 'POST', token,
      body: JSON.stringify({ ...body, brand_id: brandId }),
    }),
  ownerMembershipTierPatch: (token, tierId, body) =>
    request(`/api/owner/memberships/tiers/${tierId}`, {
      method: 'PATCH', token, body: JSON.stringify(body),
    }),
  ownerMembershipTierDelete: (token, tierId) =>
    request(`/api/owner/memberships/tiers/${tierId}`, { method: 'DELETE', token }),

  ownerMembershipPlans: (token, brandId) =>
    request(`/api/owner/memberships/plans${qsFromFilters({ brand_id: brandId })}`, { token }),
  ownerMembershipPlanCreate: (token, brandId, body) =>
    request(`/api/owner/memberships/plans${qsFromFilters({ brand_id: brandId })}`, {
      method: 'POST', token,
      body: JSON.stringify({ ...body, brand_id: brandId, plan_type: 'purchased' }),
    }),
  ownerMembershipPlanPatch: (token, planId, body) =>
    request(`/api/owner/memberships/plans/${planId}`, {
      method: 'PATCH', token, body: JSON.stringify(body),
    }),
  ownerMembershipPlanDelete: (token, planId) =>
    request(`/api/owner/memberships/plans/${planId}`, { method: 'DELETE', token }),

  ownerMembershipBenefits: (token, planId) =>
    request(`/api/owner/memberships/plans/${planId}/benefits`, { token }),
  ownerMembershipBenefitCreate: (token, planId, body) =>
    request(`/api/owner/memberships/plans/${planId}/benefits`, {
      method: 'POST', token, body: JSON.stringify(body),
    }),
  ownerMembershipBenefitPatch: (token, benefitId, body) =>
    request(`/api/owner/memberships/benefits/${benefitId}`, {
      method: 'PATCH', token, body: JSON.stringify(body),
    }),
  ownerMembershipBenefitDelete: (token, benefitId) =>
    request(`/api/owner/memberships/benefits/${benefitId}`, { method: 'DELETE', token }),

  ownerMembershipMembers: (token, brandId, filters = {}) =>
    request(
      `/api/owner/memberships/members${qsFromFilters({ brand_id: brandId, ...filters })}`,
      { token },
    ),
  ownerMembershipMember: (token, membershipId) =>
    request(`/api/owner/memberships/members/${membershipId}`, { token }),
  ownerMembershipAssign: (token, brandId, body) =>
    request(`/api/owner/memberships/assign${qsFromFilters({ brand_id: brandId })}`, {
      method: 'POST', token,
      body: JSON.stringify({ ...body, brand_id: brandId }),
    }),
  ownerMembershipCancel: (token, membershipId, reason) =>
    request(`/api/owner/memberships/members/${membershipId}/cancel`, {
      method: 'POST', token, body: JSON.stringify({ reason }),
    }),

  ownerMembershipDashboard: (token, brandId) =>
    request(
      `/api/owner/memberships/dashboard${qsFromFilters({ brand_id: brandId })}`,
      { token },
    ),

  // Owner CRM — list memberships scoped to (caller's brand, customer_id).
  // Reuses the cashier endpoint which already accepts brand_owner/super_admin
  // and is brand-scoped via JWT; the cleaner path than client-side filtering
  // a brand-wide member list.
  ownerCustomerMemberships: (token, customerId) =>
    request(`/api/cashier/memberships/members/${customerId}`, { token }),

  // Customer-facing
  customerMyMemberships: (token) => request('/api/me/memberships', { token }),
  customerMyMembership: (token, id) => request(`/api/me/memberships/${id}`, { token }),

  // ===========================================================================
  // OneStamp Loyalty V1 — L2 Bonus stamp endpoints (owner + super_admin)
  // ---------------------------------------------------------------------------
  // Backend: /app/backend/bonus_stamps.py
  // Contract locked by L2 (2026-06-25):
  //   • `idempotency_key` is client-supplied — reuse the SAME key across
  //     retries so the second call is a no-op cache hit (24h TTL).
  //   • Body fields whitelist: {idempotency_key, amount, source, reason, notes?}
  //     — extras (auto_stamp / bonus_stamp / final_stamp / stamps_earned) → 422.
  //   • Endpoint B (`ownerGrantStandaloneBonus`) needs `?brand_id=` for
  //     super_admin; brand_owner may omit it (server derives from JWT).
  // ===========================================================================
  ownerGrantBonusOnTxn: (token, transactionId, body) =>
    request(
      `/api/owner/transactions/${encodeURIComponent(transactionId)}/bonus-stamp`,
      { method: 'POST', token, body: JSON.stringify(body) },
    ),
  ownerGrantStandaloneBonus: (token, customerId, body, brandId = null) =>
    request(
      `/api/owner/customers/${encodeURIComponent(customerId)}/bonus-stamp${
        brandId ? `?brand_id=${encodeURIComponent(brandId)}` : ''
      }`,
      { method: 'POST', token, body: JSON.stringify(body) },
    ),
};

export function resolveImageUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${BASE_URL}${path}`;
}
